/**
 * Which paths Google Analytics is allowed to see.
 *
 * ALLOW-LIST, not a deny-list, and the polarity is the whole point. An earlier
 * version excluded the private areas by prefix and tracked everything else.
 * Review found that `/auth/callback` was therefore tracked - and its URL
 * carries `accessToken`, `refreshToken` and `email` - so a live bearer token
 * and a named user would have been sent to Google. `/forms/<slug>/respond/
 * success` (a customer's reference number) and `/archive/*` were tracked for
 * the same reason.
 *
 * That is the failure mode of a deny-list: whoever adds the next
 * token-bearing route has to remember, and if they forget nothing complains.
 * With an allow-list, forgetting means a marketing page is missing from a
 * report somebody is actively reading. Missing statistic, not a leak.
 *
 * `analyticsScope.test.ts` walks the generated route tree and asserts that
 * EVERY route is either on this list or deliberately excluded, so a new route
 * fails the build until someone has classified it.
 *
 * The path is only half of what GA4 receives: `page_location` carries the query
 * string too, so `analyticsQueryString` below applies the same polarity to
 * parameters.
 */

/** Public marketing pages, exactly. Sub-paths are matched on a `/` boundary. */
const TRACKED_PATHS = [
  "/",
  "/about",
  "/brand",
  "/connectors",
  "/contact",
  "/industries",
  "/pricing",
  "/proof-of-concept",
  "/resources",
  "/solutions",
  "/status",
  "/videos",
  "/legal",
  "/products",
] as const;

/**
 * Tracked on an EXACT match only - sub-paths are not included.
 *
 * `/portal/sign-up` sits behind the portal prefix but in front of the sign-in:
 * it is where a marketing visit turns into an account, so leaving it out meant
 * the funnel had no endpoint. `/portal/sign-in` is deliberately absent - it is
 * returning users arriving at the app, which tells you nothing about the
 * marketing site and would swamp the numbers.
 *
 * Exact, not prefix, because everything else under `/portal/` is the signed-in
 * application. A prefix rule here would track any future `/portal/sign-up/*`
 * step by default, which is the polarity this module exists to avoid.
 */
const TRACKED_EXACT = ["/portal/sign-up"] as const;

/**
 * Paths under a tracked prefix that must NOT be tracked after all.
 *
 * `/products/myapp/excel-addin-setup` is a support page reached from inside the
 * add-in; it is noindexed and not part of the marketing funnel.
 */
const EXCEPTIONS = ["/products/myapp/excel-addin-setup"] as const;

/**
 * Query parameters GA4 may receive, and nothing else.
 *
 * `page_location` includes the query string, so the path allow-list alone is
 * not enough. `/portal/sign-up` is reached from an invitation as
 * `?returnTo=...&inviteEmail=<address>&inviteOrg=<organisation>` - tracking the
 * path while forwarding the raw query would have sent a real person's email
 * address and their organisation's name to Google, which is the same class of
 * leak the path allow-list was written to close.
 *
 * Filtering only that one page would leave the next such parameter to whoever
 * remembers, so the rule applies to every tracked page. What survives is
 * campaign attribution, which is the only thing GA4 needs from a URL: it parses
 * these itself to populate source, medium and campaign.
 *
 * Not on the list, deliberately: `/contact`'s `systems` and `source`. They are
 * internal routing hints for the lead form rather than attribution, and adding
 * a key here later is one line.
 */
const TRACKED_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid", // Google Ads
  "gbraid",
  "wbraid",
  "msclkid", // Microsoft Ads
  "fbclid", // Meta
]);

function matches(pathname: string, prefix: string): boolean {
  if (prefix === "/") return pathname === "/";
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isAnalyticsTrackedPath(pathname: string): boolean {
  // The router matches case-insensitively (createRouter is not given
  // caseSensitive), so "/Portal/..." renders the portal perfectly well. A
  // case-sensitive comparison here would have tracked it.
  const path = pathname.toLowerCase();

  if (EXCEPTIONS.some((prefix) => matches(path, prefix))) return false;
  if (TRACKED_EXACT.some((exact) => path === exact)) return true;
  return TRACKED_PATHS.some((prefix) => matches(path, prefix));
}

/**
 * The part of a query string that may be reported to GA4, as a `?`-prefixed
 * string, or `""` when nothing survives.
 *
 * Takes `window.location.search` (with or without the leading `?`).
 */
export function analyticsQueryString(search: string): string {
  if (!search) return "";

  const kept = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    // Lower-cased on read only: some ad platforms are inconsistent about case,
    // and the key is reproduced verbatim so GA4 sees what the visitor arrived
    // with.
    if (TRACKED_QUERY_PARAMS.has(key.toLowerCase())) kept.append(key, value);
  }

  const query = kept.toString();
  return query ? `?${query}` : "";
}
